'use client';

import { Button } from '@vk-cloud-solutions/marketing-ui';
import classNames from 'classnames';
import Link from 'next/link';
import ArrowRight from 'public/static/images/icons/Arrow_right.svg';
import DropdownArrow from 'public/static/images/icons/dropdown_arrow_white.svg';
import React, { useMemo, useRef, useState } from 'react';

import { Dropdown } from '~components/Dropdown/Dropdown';
import { Image } from '~components/Image/Image';
import { ButtonConsult } from '~containers/Buttons/ButtonConsult/ButtonConsult';
import { useBreakpoints } from '~hooks/useBreakpoints';
import { useClickOutside } from '~hooks/useClickOutside';
import { ThemesNames } from '~types/Theme.types';
import { URLS } from '~types/Urls.types';

import styles from './HeaderCloudCatalog.module.scss';
import {
  HeaderCloudCatalogBannerProps,
  HeaderCloudCatalogItem,
  HeaderCloudCatalogProps,
  HeaderCloudCatalogService,
} from './HeaderCloudCatalog.types';

const PRODUCTS_ID = 'products';
const DropdownItem = ({ item }: { item: HeaderCloudCatalogService }) => (
  <>
    <div className={styles.serviceBody}>
      <div className={styles.serviceTitle}>{item.title}</div>
      {item.text && <div className={styles.serviceText}>{item.text}</div>}
    </div>
    {item.image && (
      <div className={styles.serviceIcon}>
        <Image {...item.image} alt={item.title} className={styles.icon} />
        {item.imageHover && (
          <Image
            {...item.imageHover}
            alt={`${item.title}_hover`}
            className={styles.iconHover}
          />
        )}
      </div>
    )}
  </>
);
const CasesBlock = ({ cases }: { cases: HeaderCloudCatalogItem['cases'] }) => (
  <>
    <div className={styles.cases}>
      <div className={styles.listTitle}>Лента</div>
      <div className={styles.casesList}>
        {cases?.map((caseItem) => (
          <Link
            className={classNames(styles.case, {
              [styles.hasImage]: caseItem.logo,
            })}
            href={caseItem.href}
            key={caseItem.id}
          >
            {caseItem.logo && (
              <div className={styles.caseLogoContainer}>
                {caseItem.logo[0] && (
                  <Image
                    {...caseItem.logo[0]}
                    className={styles.caseLogo}
                    alt="Case Logo"
                  />
                )}
              </div>
            )}
            <div className={styles.caseContent}>
              {caseItem.logo && (
                <div className={styles.caseTitle}>{caseItem.title}</div>
              )}
              <div className={styles.caseDesc}>{caseItem.description}</div>
              <div className={styles.caseLink}>
                Читать кейс
                <ArrowRight className={styles.iconArrow} />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </>
);
const BannerBlock = ({
  title,
  text,
  link,
  image,
}: HeaderCloudCatalogBannerProps & {
  mod: ThemesNames;
}) => (
  <div className={styles.banner}>
    <div className={styles.bannerContent}>
      <div className={styles.bannerTitle}>{title}</div>
      <p className={styles.bannerText}>{text}</p>
      {link?.href && (
        <Link className={styles.bannerLink} href={link.href}>
          Подробнее
          <ArrowRight className={styles.iconArrow} />
        </Link>
      )}
    </div>
    <div className={styles.bannerImageContainer}>
      {image && (
        <Image {...image} className={styles.bannerImage} alt="Banner img" />
      )}
    </div>
  </div>
);

export const HeaderCloudCatalog = ({
  items,
  isOpen,
  onClose,
  mod = ThemesNames.White,
}: HeaderCloudCatalogProps) => {
  const [current, setCurrent] = useState(items ? items[0] : null);
  const [activeIds, setActiveIds] = useState([PRODUCTS_ID]);
  const ref = useRef(null);
  const [, { isDesktop }] = useBreakpoints();
  const values = useMemo(() => {
    return items?.map((item) => ({
      text: item.title,
      value: String(item.id),
    }));
  }, [items]);
  const casesData = useMemo(() => current?.cases, [current]);
  const handleOpenAccordion = (id: string) => {
    const tempArr = [...activeIds];

    if (activeIds.includes(id)) {
      return setActiveIds(tempArr.filter((item) => item !== id));
    }

    tempArr.push(id);
    setActiveIds(tempArr);
  };
  const handleChange = (value: HeaderCloudCatalogItem['id']) => {
    const section = items?.find((item) => String(item.id) === value);

    if (section) {
      setCurrent(section);
    }
  };

  useClickOutside(ref, onClose, isOpen && isDesktop);

  return (
    <div
      className={classNames(styles.catalog, {
        [styles.active]: isOpen,
        [styles[mod]]: mod,
      })}
      ref={ref}
    >
      <div className={styles.content}>
        {current && (
          <>
            {!isDesktop && (
              <div className={styles.menuItem}>
                <div
                  className={classNames(styles.accordionHead, {
                    [styles.accordionHeadActive]:
                      activeIds.includes(PRODUCTS_ID),
                  })}
                  onClick={() => handleOpenAccordion(PRODUCTS_ID)}
                >
                  <div className={styles.listTitle}>Продукты</div>
                  <DropdownArrow className={styles.accordionArrow} />
                </div>
                <div
                  className={classNames(styles.accordionContent, {
                    [styles.accordionContentActive]:
                      activeIds.includes(PRODUCTS_ID),
                  })}
                >
                  <div
                    className={classNames(
                      styles.head,
                      styles.hasAllServicesBtn,
                    )}
                  >
                    <Dropdown
                      value={String(current?.id)}
                      values={values || []}
                      onChange={handleChange}
                      className={styles.select}
                      label="Продукты"
                    />
                    <Button mod="secondary" href={URLS.CloudPlatform}>
                      Все сервисы VK Cloud
                    </Button>
                  </div>
                  <ul className={styles.list}>
                    {current?.banner?.id &&
                      current?.banner?.position === 'top' && (
                        <li
                          key={current.banner.id}
                          className={styles.listItemBig}
                        >
                          <BannerBlock mod={mod} {...current.banner} />
                        </li>
                      )}
                    {current?.dropdown?.map((service) => (
                      <li className={styles.listItem} key={service.id}>
                        {service.link?.href ? (
                          <Link
                            className={styles.service}
                            href={service.link.href}
                          >
                            <DropdownItem item={service} />
                          </Link>
                        ) : (
                          <div className={styles.service}>
                            <DropdownItem item={service} />
                          </div>
                        )}
                      </li>
                    ))}
                    {current?.banner?.id &&
                      current?.banner?.position === 'bottom' && (
                        <li
                          key={current.banner.id}
                          className={styles.listItemBig}
                        >
                          <BannerBlock mod={mod} {...current.banner} />
                        </li>
                      )}
                  </ul>
                </div>
              </div>
            )}
            {isDesktop && (
              <>
                <div className={styles.btns}>
                  {values?.map((item) => (
                    <button
                      key={item.value}
                      className={classNames(styles.navItem, {
                        [styles.active]: String(current.id) === item.value,
                      })}
                      onClick={() => handleChange(item.value)}
                    >
                      {item.text}
                    </button>
                  ))}
                </div>
                <div className={styles.rightCol}>
                  <div className={styles.listTitle} key="title">
                    {current.title}
                  </div>
                  {current?.banner?.id &&
                    current?.banner?.position === 'top' && (
                      <BannerBlock mod={mod} {...current.banner} />
                    )}
                  <ul className={styles.list}>
                    {current?.dropdown?.map((service) => (
                      <li className={styles.listItem} key={service.id}>
                        {service.link?.href ? (
                          <Link
                            className={styles.service}
                            href={service.link.href}
                          >
                            <DropdownItem item={service} />
                          </Link>
                        ) : (
                          <div className={styles.service}>
                            <DropdownItem item={service} />
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                  {current?.banner?.id &&
                    current?.banner?.position === 'bottom' && (
                      <BannerBlock mod={mod} {...current.banner} />
                    )}
                  {casesData && casesData.length > 0 && (
                    <CasesBlock cases={casesData} />
                  )}
                </div>
              </>
            )}
          </>
        )}
        {!isDesktop && (
          <div key="consult" className={styles.menuItem}>
            <ButtonConsult
              className={styles.buttonConsult}
              dataTest="header.consultationButton"
              formLabel="main-menu"
            />
          </div>
        )}
        {casesData && casesData.length > 0 && !isDesktop && (
          <CasesBlock cases={casesData} />
        )}
      </div>
      <div className={styles.bottom}>
        {isDesktop && (
          <Link className={styles.allServicesLink} href={URLS.CloudPlatform}>
            Все сервисы VK Cloud
            <ArrowRight className={styles.iconArrow} />
          </Link>
        )}
        <div className={styles.bottomLinks}>
          <Link className={styles.bottomLink} href={URLS.Blog}>
            Блог
          </Link>
          <Link className={styles.bottomLink} href={URLS.SuccessStory}>
            Истории успеха
          </Link>
          <Link className={styles.bottomLink} href={URLS.Docs}>
            Помощь
          </Link>
        </div>
      </div>
    </div>
  );
};
